import { useEffect, useMemo, useRef, useState } from 'react';
import { CardBack, CardFace, IntegrityBar, MiniCard, initials } from '../components/parts';
import { CardCodex } from '../components/CardCodex';
import { CardDetail } from '../components/CardDetail';
import { RolePanel } from '../components/RolePanel';
import { PlayerInfo } from '../components/PlayerInfo';
import { CardInfo } from '../components/CardInfo';
import { Coach } from '../components/Coach';
import { ScreenFlash } from '../components/ScreenFlash';
import { CardIcon, RoleIcon } from '../components/Icon';
import { AvatarFrame } from '../components/Frames';
import type { FeedItem, GameSession } from '../game/useGameSession';
import type { Card, CardKind, PublicPlayer } from '../game/types';
import { needsTarget, parseLogEvent } from '../game/types';
import { cardSuit, characterName, roleColor, roleComposition, roleOrder } from '../game/cardMeta';
import { colorForId } from '../game/cosmetics';
import { cardArt, characterArt, roleArt } from '../game/art';
import { cardBackCss, savedCardBack } from '../game/shop';
import type { Role } from '../game/types';
import { playSound, setMusicMode, vibrate } from '../feedback';
import { renderLogLine, useT } from '../i18n';

interface Bolt {
  id: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Jagged lightning polyline between the two seats (0–100 table space). */
  points: string;
  /** Impact spark directions, degrees. */
  sparks: number[];
  /** 'handoff' renders as a soft cyan signal pulse instead of an attack bolt. */
  variant?: 'handoff';
}

/** Center-screen role reveal beat: death (flat) or a Trace exposure. */
interface Ceremony {
  id: number;
  uid: string;
  role: Role;
  kind: 'flat' | 'trace';
}

// A lightning arc: perpendicular jitter that bows out mid-flight and pinches
// at both ends, so the bolt reads as energy rather than a ruler line.
function jaggedPoints(x0: number, y0: number, x1: number, y1: number): string {
  const segs = 7;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const pts = [`${x0},${y0}`];
  for (let i = 1; i < segs; i++) {
    const t = i / segs;
    const amp = Math.sin(Math.PI * t) * len * 0.14;
    const off = (Math.random() * 2 - 1) * amp;
    pts.push(`${x0 + dx * t + nx * off},${y0 + dy * t + ny * off}`);
  }
  pts.push(`${x1},${y1}`);
  return pts.join(' ');
}

interface DuelBeat {
  id: number;
  attackerUid: string;
  targetUid: string;
  loserUid: string;
}


// Landscape media query — must match the breakpoint in theme.css so the JS seat
// layout and the CSS grid flip together.
const LANDSCAPE_MQ = '(orientation: landscape) and (min-width: 640px)';

function useIsLandscape(): boolean {
  const [match, setMatch] = useState(() => window.matchMedia(LANDSCAPE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(LANDSCAPE_MQ);
    const onChange = () => setMatch(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return match;
}

export function Table({ session }: { session: GameSession }) {
  const t = useT();
  const { game, players, me, myUid, hand, isMyTurn, mustRespond, myRole } = session;
  const [selected, setSelected] = useState<Card | null>(null);
  const [discards, setDiscards] = useState<Set<string>>(new Set());
  const [showRole, setShowRole] = useState(false);
  const [showCodex, setShowCodex] = useState(false);
  // Long-press a hand card to inspect its full art (view-only, mid-game).
  const [inspect, setInspect] = useState<CardKind | null>(null);
  const [infoPlayer, setInfoPlayer] = useState<PublicPlayer | null>(null);
  const [cardInfo, setCardInfo] = useState<Card | null>(null);
  const [flatlineSeen, setFlatlineSeen] = useState(false);
  const [ceremony, setCeremony] = useState<Ceremony | null>(null);
  const ceremonyQ = useRef<Ceremony[]>([]);
  // Bumped on every push; the effect below drains the queue. Kept OUT of the
  // setCeremony updater — updaters must stay pure (StrictMode runs them twice).
  const [ceremonyTick, setCeremonyTick] = useState(0);
  const [shakeId, setShakeId] = useState<number | null>(null);
  const [empWave, setEmpWave] = useState<{ id: number; x: number; y: number } | null>(null);
  const [reasonToast, setReasonToast] = useState<{ id: number; text: string } | null>(null);
  // A card being played flies from its owner's seat into the ring centre, spins,
  // and fades. fromX/fromY are the seat position as a % of the table box.
  const [flyCard, setFlyCard] = useState<{ card: Card; fromX: number; fromY: number; id: number } | null>(null);
  const [duelBeat, setDuelBeat] = useState<DuelBeat | null>(null);
  // Raid/Siphon on a target that has face-up gear: pick which card to take.
  const [rob, setRob] = useState<{ card: Card; target: PublicPlayer } | null>(null);
  const [gift, setGift] = useState<{ card: Card; target: PublicPlayer } | null>(null);
  const [rebootCard, setRebootCard] = useState<Card | null>(null);
  // Whether we've deployed any card this turn — gates the "sure?" end-turn prompt.
  const [playedThisTurn, setPlayedThisTurn] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const flyIdRef = useRef(0);
  const lastPlayedIdRef = useRef<string | null>(null);
  const lastDiscardIdRef = useRef<string | null>(null);
  const lastDuelLogRef = useRef<string | null>(null);
  const FLY_MS = 1150; // matches the 1.05s card-throw animation + a little slack

  // A fresh turn resets the "played a card" tracker so the prompt is per-turn.
  useEffect(() => {
    setPlayedThisTurn(false);
    setConfirmEnd(false);
  }, [game?.turnCount]);

  // Play a card with a flourish: it flies from my seat into the ring centre.
  function doPlay(card: Card, target: PublicPlayer | null, targetCardId?: string, cardIds?: string[]) {
    lastPlayedIdRef.current = card.id; // so the discard watcher won't replay it
    flyFrom(card, myUid);
    playSound('tap');
    setPlayedThisTurn(true);
    session.playCard(card, target, targetCardId, cardIds);
  }

  // End-turn tap: if no card was deployed this turn, ask for confirmation first.
  function requestEndTurn() {
    if (playedThisTurn) session.endTurn();
    else setConfirmEnd(true);
  }

  // Seat coordinates on the table (0–100 space).
  // Portrait: a full circle with me pinned at the bottom.
  // Landscape: a flat ellipse. The bottom-center is left empty (the hub piles
  // live there on short screens), so seats sweep from bottom-left over the top
  // to bottom-right — me first, at the bottom-left.
  const isLandscape = useIsLandscape();
  // Ring geometry as a % of the table box. cx/cy is the centre (nudged down in
  // portrait to sit on the arena's neon ring, which is wider than tall).
  const ring = useMemo(
    () => ({ cx: 50, cy: isLandscape ? 50 : 58, rx: isLandscape ? 41 : 39, ry: 30 }),
    [isLandscape],
  );
  const coords = useMemo(() => {
    const n = players.length || 1;
    const myIdx = Math.max(0, players.findIndex((p) => p.uid === myUid));
    const start = isLandscape ? Math.PI * (140 / 180) : Math.PI / 2;
    const sweep = isLandscape ? Math.PI * (300 / 180) : Math.PI * 2;
    const map = new Map<string, { x: number; y: number }>();
    players.forEach((p, i) => {
      const slot = ((i - myIdx) % n + n) % n;
      const ang = start + (slot * sweep) / n;
      map.set(p.uid, { x: ring.cx + ring.rx * Math.cos(ang), y: ring.cy + ring.ry * Math.sin(ang) });
    });
    return map;
  }, [players, myUid, isLandscape, ring]);

  // Launch the play animation for `card` from `uid`'s seat toward the centre.
  function flyFrom(card: Card, uid: string) {
    const from = coords.get(uid);
    if (!from) return;
    flyIdRef.current += 1;
    const id = flyIdRef.current;
    setFlyCard({ card, fromX: from.x, fromY: from.y, id });
    setTimeout(() => setFlyCard((f) => (f?.id === id ? null : f)), FLY_MS);
  }

  // Animate OTHER players' plays: when a new card lands on the discard pile and
  // it wasn't our own (already animated in doPlay), throw it from the active
  // seat into the centre. Seed the ref on mount so a resume doesn't replay.
  useEffect(() => {
    const top = game?.discardTop;
    if (!top) return;
    if (lastDiscardIdRef.current === null) {
      lastDiscardIdRef.current = top.id;
      return;
    }
    if (top.id === lastDiscardIdRef.current) return;
    lastDiscardIdRef.current = top.id;
    if (top.id === lastPlayedIdRef.current) return; // mine — already flew
    const active = players.find((p) => p.seatIndex === game?.activeSeat);
    if (active && active.uid !== myUid) flyFrom(top, active.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.discardTop?.id]);

  // --- FX: seat flashes + flying spike bolts --------------------------------
  const [fxClass, setFxClass] = useState<Record<string, string>>({});
  const [bolts, setBolts] = useState<Bolt[]>([]);
  const [dmgFloats, setDmgFloats] = useState<{ id: number; uid: string; amount: number }[]>([]);
  const [splat, setSplat] = useState<{ id: number; amount: number } | null>(null);
  // Attacker lunge offsets (px), keyed by uid — feeds --lunge-x/--lunge-y.
  const [lunges, setLunges] = useState<Record<string, { x: number; y: number }>>({});
  const seenFx = useRef(0);
  // When a spike bolt just flew at a seat, that seat's hit/block reaction is
  // delayed to land together with the bolt's impact burst.
  const spikeHitAt = useRef(new Map<string, number>());

  useEffect(() => {
    // Flash a reaction class on a seat; the node's key includes the class so
    // the element remounts and the animation restarts.
    function flashSeat(uid: string, cls: string, id: number, ms: number) {
      setFxClass((m) => ({ ...m, [uid]: cls + '#' + id }));
      setTimeout(
        () => setFxClass((m) => {
          const n = { ...m };
          if (n[uid]?.endsWith('#' + id)) delete n[uid];
          return n;
        }),
        ms,
      );
    }

    for (const e of session.fx) {
      if (e.id <= seenFx.current) continue;
      seenFx.current = e.id;

      if (e.kind.type === 'spike') {
        const a = coords.get(e.kind.fromUid);
        const to = coords.get(e.kind.toUid);
        if (a && to) {
          const bolt: Bolt = {
            id: e.id,
            x0: a.x,
            y0: a.y,
            x1: to.x,
            y1: to.y,
            points: jaggedPoints(a.x, a.y, to.x, to.y),
            sparks: Array.from({ length: 6 }, (_, i) => i * 60 + Math.random() * 30 - 15),
          };
          setBolts((b) => [...b, bolt]);
          setTimeout(() => setBolts((b) => b.filter((x) => x.id !== e.id)), 620);
          // Attacker lunges toward the target as the bolt fires.
          const from = e.kind.fromUid;
          const len = Math.hypot(to.x - a.x, to.y - a.y) || 1;
          setLunges((m) => ({
            ...m,
            [from]: { x: ((to.x - a.x) / len) * 18, y: ((to.y - a.y) / len) * 18 },
          }));
          flashSeat(from, 'fx-lunge', e.id, 450);
          spikeHitAt.current.set(e.kind.toUid, Date.now());
        }
        playSound('zap');
        continue;
      }

      if (e.kind.type === 'handoff') {
        const a = coords.get(e.kind.fromUid);
        const to = coords.get(e.kind.toUid);
        if (a && to) {
          const bolt: Bolt = {
            id: e.id,
            x0: a.x,
            y0: a.y,
            x1: to.x,
            y1: to.y,
            points: jaggedPoints(a.x, a.y, to.x, to.y),
            sparks: [],
            variant: 'handoff',
          };
          setBolts((b) => [...b, bolt]);
          setTimeout(() => setBolts((b) => b.filter((x) => x.id !== e.id)), 700);
        }
        continue;
      }

      if (e.kind.type === 'death') {
        const dead = e.kind;
        flashSeat(dead.uid, 'fx-death', e.id, 1600);
        setShakeId(e.id);
        setTimeout(() => setShakeId((s) => (s === e.id ? null : s)), 650);
        ceremonyQ.current.push({ id: e.id, uid: dead.uid, role: dead.role, kind: 'flat' });
        setCeremonyTick((n) => n + 1);
        playSound('death');
        vibrate([60, 40, 90]);
        continue;
      }

      if (e.kind.type === 'reveal') {
        const rev = e.kind;
        ceremonyQ.current.push({ id: e.id, uid: rev.uid, role: rev.role, kind: 'trace' });
        setCeremonyTick((n) => n + 1);
        playSound('chime');
        continue;
      }

      if (e.kind.type === 'emp') {
        const from = coords.get(e.kind.fromUid);
        const src = e.kind.fromUid;
        if (from) {
          setEmpWave({ id: e.id, x: from.x, y: from.y });
          setTimeout(() => setEmpWave((w) => (w?.id === e.id ? null : w)), 1200);
          // The zap ripples outward: seats flinch in distance order.
          for (const p of session.players) {
            if (p.uid === src || !p.alive) continue;
            const c = coords.get(p.uid);
            if (!c) continue;
            const dist = Math.hypot(c.x - from.x, c.y - from.y);
            setTimeout(() => flashSeat(p.uid, 'fx-empzap', e.id, 700), 120 + dist * 9);
          }
        }
        playSound('boom');
        vibrate(80);
        continue;
      }

      if (e.kind.type === 'plantTick') {
        flashSeat(e.kind.uid, 'fx-virustick', e.id, 900);
        playSound('tick');
        continue;
      }

      const uid = 'uid' in e.kind ? e.kind.uid : null;
      if (!uid) continue;
      const kind = e.kind;
      const eid = e.id;
      // Bolt comet reaches the target ~0.2s in; hold the reaction until then.
      const sinceSpike = Date.now() - (spikeHitAt.current.get(uid) ?? 0);
      const delay = sinceSpike < 600 ? 240 : 0;
      const apply = () => {
        const cls =
          kind.type === 'heal' ? 'fx-heal' : kind.type === 'blocked' ? 'fx-block' : 'fx-hit';
        // heal glyphs rise for ~1.3s (0.9s + stagger); hit's magenta bloom is 0.7s
        flashSeat(uid, cls, eid, kind.type === 'heal' ? 1400 : 750);
        if (kind.type === 'blocked') playSound('shield');
        if (kind.type === 'damage') {
          const amount = kind.amount;
          setDmgFloats((l) => [...l.slice(-6), { id: eid, uid, amount }]);
          setTimeout(() => setDmgFloats((l) => l.filter((x) => x.id !== eid)), 1100);
          playSound('hit');
          if (uid === myUid) {
            setSplat({ id: eid, amount });
            setTimeout(() => setSplat((s) => (s?.id === eid ? null : s)), 900);
            vibrate([45, 30, 45]);
          }
        }
      };
      if (delay) setTimeout(apply, delay);
      else apply();
    }
  }, [session.fx, coords]);

  useEffect(() => {
    if (mustRespond) {
      playSound('zap');
      vibrate(60);
    }
  }, [mustRespond]);

  // Ceremony queue: show one reveal at a time for ~2.6s, then the next.
  useEffect(() => {
    if (ceremony) {
      const timer = setTimeout(() => setCeremony(null), 2600);
      return () => clearTimeout(timer);
    }
    const next = ceremonyQ.current.shift();
    if (next) setCeremony(next);
    return undefined;
  }, [ceremony, ceremonyTick]);

  // "Your turn" alert: chirp + buzz on the transition, and flash the tab title
  // while it stays your turn (so a backgrounded tab is noticeable).
  const wasMyTurn = useRef(false);
  useEffect(() => {
    if (isMyTurn && !wasMyTurn.current) {
      playSound('chime');
      vibrate(30);
    }
    wasMyTurn.current = isMyTurn;
  }, [isMyTurn]);
  useEffect(() => {
    if (!isMyTurn) return;
    const alt = `⚡ ${t.table.yourTurnTitle}`;
    const iv = setInterval(() => {
      document.title = document.title === alt ? 'NEONFALL' : alt;
    }, 1000);
    return () => {
      clearInterval(iv);
      document.title = 'NEONFALL';
    };
  }, [isMyTurn, t]);

  useEffect(() => {
    if (game?.winner) playSound('win');
  }, [game?.winner]);

  useEffect(() => {
    if (!game?.turnLog.length) return;
    const latestDuel = [...game.turnLog].reverse().find((line) => parseLogEvent(line)?.t === 'duel') ?? null;
    if (!latestDuel || latestDuel === lastDuelLogRef.current) return;
    if (lastDuelLogRef.current === null) {
      lastDuelLogRef.current = latestDuel;
      return;
    }
    lastDuelLogRef.current = latestDuel;
    const ev = parseLogEvent(latestDuel);
    if (!ev || ev.t !== 'duel') return;
    const id = Date.now();
    setDuelBeat({ id, attackerUid: ev.a, targetUid: ev.b, loserUid: ev.l });
    playSound('zap');
    setMusicMode('duel');
    setTimeout(() => {
      setDuelBeat((beat) => (beat?.id === id ? null : beat));
      setMusicMode('ambient');
    }, 3600);
  }, [game?.turnLog]);

  if (!game) {
    return <div className="screen center"><div className="mono-dim">{t.table.syncing}</div></div>;
  }

  const phase = game.phase;
  const excessDiscard = me ? Math.max(0, hand.length - me.integrity) : 0;
  const activePlayer = players.find((p) => p.seatIndex === game.activeSeat) ?? null;
  // Sudden death: grid surges hit every turn once it starts, so the sliding
  // log tail keeps a 'surge' line for as long as the mode lasts.
  const suddenDeath =
    !game.winner && game.turnLog.some((l) => parseLogEvent(l)?.t === 'surge');

  function onSeatClick(target: PublicPlayer) {
    // Targeting mode: a card that needs a target is selected and this seat is
    // in range → play it. Otherwise open the player's info card.
    if (selected && needsTarget(selected.kind) && session.canTarget(target, selected)) {
      // Raid/Siphon on a target with face-up gear → let the player choose what
      // to take (a specific piece of gear, or a random card from the hand).
      if (
        (selected.kind === 'raid' || selected.kind === 'siphon' || selected.kind === 'killSwitch') &&
        target.equippedGear.length > 0
      ) {
        setRob({ card: selected, target });
        setSelected(null);
        return;
      }
      if (selected.kind === 'backchannel') {
        setGift({ card: selected, target });
        setSelected(null);
        return;
      }
      doPlay(selected, target);
      setSelected(null);
      return;
    }
    setInfoPlayer(target);
  }

  function onCardClick(card: Card) {
    if (phase === 'discard') {
      setDiscards((s) => {
        const n = new Set(s);
        if (n.has(card.id)) n.delete(card.id);
        else n.add(card.id);
        return n;
      });
      return;
    }
    if (!isMyTurn || phase !== 'play') return;
    setSelected((cur) => (cur?.id === card.id ? null : card));
  }

  // Deck (face-down) + discard (face-up). Lives in the centre hub on wide
  // screens; on portrait it docks compactly above the hand so the seat ring
  // keeps only phase/timer/turn in the middle.
  const pilesEl = (
    <div className="piles">
      <div className="pile">
        <div className="deck-stack">
          <CardBack className="stack s3" />
          <CardBack className="stack s2" />
          <CardBack className="stack s1" />
          <span className="deck-count">{game?.deckCount}</span>
        </div>
        <span className="pile-label">{t.table.deck}</span>
      </div>
      <div className="pile">
        <div
          className="discard-stack"
          onClick={() => game?.discardTop && setCardInfo(game.discardTop)}
        >
          {game?.discardTop ? (
            <MiniCard key={game.discardTop.id} card={game.discardTop} />
          ) : (
            <div className="pile-empty">∅</div>
          )}
        </div>
        <span className="pile-label">{t.table.discard}</span>
      </div>
    </div>
  );

  return (
    <div className="screen table-screen">
      {/* Header */}
      <div className="row between table-head">
        <button className="link" onClick={session.leaveToHome}>{t.table.exit}</button>
        {session.spectating ? (
          <span className="role-chip spectating-chip">👁 {t.table.spectating}</span>
        ) : (
          myRole && (
            <button
              className="role-chip"
              style={{ color: roleColor[myRole], borderColor: roleColor[myRole] }}
              onClick={() => setShowRole(true)}
            >
              <RoleIcon role={myRole} size={13} /> {t.role.name[myRole]} <span className="role-chip-i">ⓘ</span>
            </button>
          )
        )}
        <button className="link" onClick={() => setShowCodex(true)}>{t.table.cards}</button>
      </div>

      {/* Faction roster — public composition + who has flatlined */}
      <RosterBar players={players} />

      {/* Round table */}
      <div className={'table-wrap' + (shakeId !== null ? ' shake' : '') + (suddenDeath ? ' sudden' : '')}>

        {/* spike bolts + turn-handoff signal pulses */}
        <svg className="fx-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {bolts.map((b) =>
            b.variant === 'handoff' ? (
              <g key={b.id}>
                <polyline className="handoff-line" points={`${b.x0},${b.y0} ${b.x1},${b.y1}`} pathLength={1} />
                <circle className="handoff-dot" r="1.1">
                  <animate attributeName="cx" from={b.x0} to={b.x1} dur="0.45s" fill="freeze" />
                  <animate attributeName="cy" from={b.y0} to={b.y1} dur="0.45s" fill="freeze" />
                </circle>
                <g transform={`translate(${b.x1} ${b.y1})`}>
                  <circle className="handoff-ring" r="2" />
                </g>
              </g>
            ) : (
            <g key={b.id}>
              <polyline className="bolt-glow" points={b.points} pathLength={1} />
              <polyline className="bolt-core" points={b.points} pathLength={1} />
              <circle className="bolt-dot" r="1.4">
                <animate attributeName="cx" from={b.x0} to={b.x1} dur="0.22s" fill="freeze" />
                <animate attributeName="cy" from={b.y0} to={b.y1} dur="0.22s" fill="freeze" />
              </circle>
              {/* impact burst at the target: expanding ring + radial sparks */}
              <g transform={`translate(${b.x1} ${b.y1})`}>
                <circle className="impact-ring" r="2.4" />
                {b.sparks.map((ang, i) => (
                  <g key={i} transform={`rotate(${ang})`}>
                    <line className="impact-spark" x1="1.5" y1="0" x2="7" y2="0" pathLength={1} />
                  </g>
                ))}
              </g>
            </g>
            ),
          )}
        </svg>

        {/* EMP shockwave expanding from the detonating seat */}
        {empWave && (
          <div
            className="emp-wave"
            key={'emp' + empWave.id}
            style={{ left: empWave.x + '%', top: empWave.y + '%' }}
          />
        )}

        {/* center hub: phase + timer + whose turn; the piles join it only on
            wide screens — portrait docks them above the hand instead. */}
        <div className="table-hub">
          <div className="phase-badge">
            <span>{t.phase[phase]}</span>
            {game.turnDeadline && !game.winner && <TurnTimer deadline={game.turnDeadline} />}
          </div>
          {suddenDeath && <div className="surge-badge">⚠ {t.table.surge}</div>}
          {isLandscape && pilesEl}
          <div className="hub-turn mono-dim">
            {activePlayer ? t.table.turnOf(activePlayer.uid === myUid ? t.player.you : activePlayer.displayName) : '—'}
          </div>
        </div>

        {/* seats */}
        {players.map((p) => {
          const c = coords.get(p.uid)!;
          const active = game.activeSeat === p.seatIndex && p.alive && !game.winner;
          const targeting = !!selected && needsTarget(selected.kind);
          const targetable = targeting && session.canTarget(p, selected!);
          // While aiming, seats you cannot reach visibly drop out of play.
          const unreachable = targeting && !targetable && p.alive && p.uid !== myUid;
          const flash = fxClass[p.uid]?.split('#')[0];
          const portrait = characterArt(p.characterId);
          return (
            <div
              key={p.uid + (fxClass[p.uid] ?? '')}
              className={
                'node' +
                (active ? ' active' : '') +
                (targetable ? ' targetable' : '') +
                (unreachable ? ' unreachable' : '') +
                (!p.alive ? ' dead' : '') +
                (p.uid === myUid ? ' me' : '') +
                (flash ? ' ' + flash : '')
              }
              style={{
                left: c.x + '%',
                top: c.y + '%',
                ...(lunges[p.uid]
                  ? {
                      ['--lunge-x' as string]: lunges[p.uid]!.x + 'px',
                      ['--lunge-y' as string]: lunges[p.uid]!.y + 'px',
                    }
                  : {}),
              }}
              onClick={() => onSeatClick(p)}
            >
              <div
                className={'node-avatar' + (p.frame ? ' frame-' + p.frame : '')}
                style={{
                  color: p.roleRevealed && p.revealedRole ? roleColor[p.revealedRole] : 'var(--dim)',
                }}
              >
                <AvatarFrame frame={p.frame} />
                {portrait ? (
                  <img className="avatar-img" src={portrait} alt="" />
                ) : p.photoURL ? (
                  <img className="avatar-img" src={p.photoURL} alt="" referrerPolicy="no-referrer" />
                ) : p.alive ? (
                  initials(p.displayName)
                ) : (
                  '✕'
                )}
                {p.characterId && (
                  <span className="node-char">{characterName[p.characterId] ?? p.characterId}</span>
                )}
                {p.roleRevealed && p.revealedRole ? (
                  <span
                    className="node-role"
                    style={{ color: roleColor[p.revealedRole], borderColor: roleColor[p.revealedRole] }}
                  >
                    <RoleIcon role={p.revealedRole} size={9} /> {t.role.name[p.revealedRole]}
                  </span>
                ) : (
                  <span className="node-role hidden">?? {t.player.unknownTag}</span>
                )}
              </div>
              {flash === 'fx-block' && (
                <span className="shield-fx" aria-hidden>
                  <span className="shield-hex" />
                  <span className="shield-ring" />
                </span>
              )}
              {flash === 'fx-heal' && (
                <span className="heal-fx" aria-hidden>
                  <span className="heal-plus">+</span>
                  <span className="heal-plus">+</span>
                  <span className="heal-plus">+</span>
                  <span className="heal-plus">+</span>
                  <span className="heal-ring" />
                </span>
              )}
              {flash === 'fx-hit' && (
                <span className="hit-fx" aria-hidden>
                  <span />
                  <span />
                </span>
              )}
              <div className="node-name">
                <span className="node-player-avatar">
                  {p.photoURL ? (
                    <img className="avatar-img" src={p.photoURL} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    initials(p.displayName)
                  )}
                </span>
                <span style={{ color: colorForId(p.nameColor) }}>
                  {p.uid === myUid ? t.player.you : p.displayName}
                </span>
              </div>

              {/* Status: integrity while alive, OUT when flatlined */}
              {p.alive ? (
                <IntegrityBar value={p.integrity} max={p.maxIntegrity} />
              ) : (
                <span className="node-out">{t.player.out}</span>
              )}

              {p.equippedGear.length > 0 && (
                <div className="node-gear">
                  {p.equippedGear.map((g, i) => (
                    <span
                      key={i}
                      className={
                        g.kind === 'virus' || g.kind === 'downtime'
                          ? `gear-plant gear-${g.kind}`
                          : undefined
                      }
                      title={t.card.name[g.kind]}
                      style={{ color: cardSuit[g.kind].color }}
                    >
                      <CardIcon kind={g.kind} size={15} />
                    </span>
                  ))}
                </div>
              )}
              <span className="node-hand-badge" title={t.player.gear}>
                <span className="hand-back" style={cardBackCss(savedCardBack())} aria-hidden />
                <span className="hand-count">{p.handCount}</span>
              </span>
            </div>
          );
        })}

        {/* floating trash-talk (emoji) and chat bubbles at the sender's seat */}
        {session.emotes.map((e) => {
          const c = coords.get(e.uid);
          if (!c) return null;
          // Bot lines arrive as an i18n key so each client shows them localized.
          const line = e.lineKey ? t.botLines[e.lineKey] : undefined;
          const body = line ?? e.text ?? e.emoji;
          if (!body) return null;
          const edgeClass = c.x > 72 ? ' edge-right' : c.x < 28 ? ' edge-left' : '';
          return (
            <div
              key={e.id}
              className={'emote-float' + (e.text || line ? ' bubble' : '') + edgeClass}
              style={{ left: c.x + '%', top: c.y + '%' }}
            >
              {body}
            </div>
          );
        })}

        {/* floating damage numbers over the hit seat */}
        {dmgFloats.map((d) => {
          const c = coords.get(d.uid);
          if (!c) return null;
          return (
            <div key={'d' + d.id} className="dmg-float" style={{ left: c.x + '%', top: c.y + '%' }}>
              -{d.amount}
            </div>
          );
        })}

        {/* a played card flying from its owner's seat into the ring centre */}
        {flyCard && (
          <>
            <div
              className="card-throw"
              key={flyCard.id}
              style={{
                ['--from-x' as string]: flyCard.fromX + '%',
                ['--from-y' as string]: flyCard.fromY + '%',
                ['--to-x' as string]: ring.cx + '%',
                ['--to-y' as string]: ring.cy + '%',
                ['--accent' as string]: cardSuit[flyCard.card.kind].color,
              }}
            >
              <div className="card-throw-arc">
                <div className="card-throw-spin">
                  <CardFace card={flyCard.card} disabled />
                </div>
              </div>
            </div>
            {/* landing shockwave at the centre, timed to the slam */}
            <div
              className="card-land-fx"
              key={'land' + flyCard.id}
              style={{
                left: ring.cx + '%',
                top: ring.cy + '%',
                ['--accent' as string]: cardSuit[flyCard.card.kind].color,
              }}
            />
          </>
        )}
      </div>

      {/* Spectators watch read-only: no hand, no actions, just a live board. */}
      {session.spectating && (
        <div className="spectator-bar mono-dim">👁 {t.table.spectatingHint}</div>
      )}

      {/* Hand */}
      {!session.spectating && (
      <div className="col hand-area" style={{ gap: 6 }}>
        {phase === 'discard' && isMyTurn && (
          <div className="row" style={{ justifyContent: 'center' }}>
            <span className="mono-dim" style={{ fontSize: 11 }}>{t.table.discardPrompt}</span>
          </div>
        )}
        <div
          className="hand"
          style={{
            ['--hand-count' as string]: hand.length,
            ['--hand-slots' as string]: Math.max(1, hand.length - 1),
            ['--fan-max-step' as string]: `${Math.max(42, 120 - hand.length * 9)}px`,
            ['--fan-min-step' as string]: `${Math.max(18, 38 - hand.length * 2)}px`,
          }}
        >
          {hand.map((c, index) => (
            <CardFace
              key={c.id}
              card={c}
              style={{
                ['--fan-index' as string]: index,
                ['--fan-mid' as string]: (hand.length - 1) / 2,
                ['--fan-offset' as string]: index - (hand.length - 1) / 2,
                ['--fan-depth' as string]: Math.abs(index - (hand.length - 1) / 2),
              }}
              selected={selected?.id === c.id || discards.has(c.id)}
              disabled={phase === 'draw' || (!isMyTurn && phase !== 'discard') || !!game.winner}
              onClick={() => onCardClick(c)}
              onInspect={() => setInspect(c.kind)}
              onDisabledClick={() => {
                if (game.winner) return;
                const text =
                  phase === 'draw' && isMyTurn ? t.table.reasonDraw : t.table.reasonNotYourTurn;
                const id = Date.now();
                setReasonToast({ id, text });
                setTimeout(() => setReasonToast((r) => (r?.id === id ? null : r)), 1800);
              }}
            />
          ))}
          {hand.length === 0 && <div className="mono-dim">{t.table.noCards}</div>}
        </div>
      </div>
      )}

      {/* Action bar */}
      {!session.spectating && (
      <div className="action-area">
        <ActionBar
          session={session}
          selected={selected}
          excessDiscard={excessDiscard}
          discards={discards}
          onEndTurn={requestEndTurn}
          onDeploy={() => {
            if (!selected) return;
            const card = selected;
            setSelected(null);
            if (card.kind === 'coldReboot') {
              setRebootCard(card);
              return;
            }
            doPlay(card, null);
          }}
          onConfirmDiscard={() => {
            session.confirmDiscard(hand.filter((c) => discards.has(c.id)));
            setDiscards(new Set());
          }}
        />
      </div>
      )}

      {rob && (
        <RobPicker
          card={rob.card}
          target={rob.target}
          allowHand={rob.card.kind !== 'killSwitch'}
          onPick={(targetCardId) => {
            doPlay(rob.card, rob.target, targetCardId);
            setRob(null);
          }}
          onClose={() => setRob(null)}
        />
      )}
      {gift && (
        <GiftPicker
          card={gift.card}
          target={gift.target}
          hand={hand.filter((c) => c.id !== gift.card.id)}
          onPick={(giftCardId) => {
            doPlay(gift.card, gift.target, giftCardId);
            setGift(null);
          }}
          onClose={() => setGift(null)}
        />
      )}
      {rebootCard && (
        <RebootPicker
          card={rebootCard}
          hand={hand.filter((c) => c.id !== rebootCard.id)}
          onConfirm={(cardIds) => {
            doPlay(rebootCard, null, undefined, cardIds);
            setRebootCard(null);
          }}
          onClose={() => setRebootCard(null)}
        />
      )}
      {duelBeat && <DuelMomentOverlay beat={duelBeat} players={players} />}
      {confirmEnd && (
        <div className="modal-backdrop" onClick={() => setConfirmEnd(false)}>
          <div className="panel center end-turn-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="subtitle">{t.table.endTurnConfirmTitle}</div>
            <div className="mono-dim" style={{ marginTop: 6 }}>{t.table.endTurnConfirmBody}</div>
            <div className="col" style={{ gap: 10, width: '100%', marginTop: 18 }}>
              <button
                className="btn block magenta"
                disabled={session.busy}
                onClick={() => {
                  setConfirmEnd(false);
                  session.endTurn();
                }}
              >
                {t.table.endTurn}
              </button>
              <button className="btn block ghost" onClick={() => setConfirmEnd(false)}>
                {t.table.endTurnKeep}
              </button>
            </div>
          </div>
        </div>
      )}
      {isMyTurn && game.phase === 'draw' && !game.winner && !mustRespond && (
        <DrawPrompt session={session} />
      )}
      {mustRespond && <RespondOverlay session={session} />}
      {showRole && myRole && (
        <RolePanel role={myRole} me={me} onClose={() => setShowRole(false)} />
      )}
      {showCodex && <CardCodex onClose={() => setShowCodex(false)} />}
      {inspect && (
        <CardDetail kind={inspect} color={cardSuit[inspect].color} onClose={() => setInspect(null)} />
      )}
      {infoPlayer && (
        <PlayerInfo player={infoPlayer} isMe={infoPlayer.uid === myUid} onClose={() => setInfoPlayer(null)} />
      )}
      {cardInfo && <CardInfo card={cardInfo} onClose={() => setCardInfo(null)} />}
      {!game.winner && !mustRespond && (
        <Coach phase={phase} isMyTurn={isMyTurn} myRole={myRole} />
      )}
      {suddenDeath && <div className="surge-vignette" aria-hidden />}
      {reasonToast && (
        <div className="reason-toast" key={reasonToast.id}>
          {reasonToast.text}
        </div>
      )}
      {/* Role reveal ceremony — the hidden-role payoff moment, one at a time. */}
      {ceremony && (
        <div className="ceremony" key={ceremony.id} aria-hidden>
          <div className="ceremony-inner" style={{ ['--accent' as string]: roleColor[ceremony.role] }}>
            <div className="ceremony-eyebrow">
              {ceremony.kind === 'flat' ? t.table.ceremonyFlat : t.table.ceremonyTrace}
            </div>
            <div className="ceremony-flip">
              <div className="ceremony-face ceremony-back" style={cardBackCss(savedCardBack())} />
              <div
                className="ceremony-face ceremony-front"
                style={{ backgroundImage: `url(${roleArt(ceremony.role)})` }}
              >
                <span className="ceremony-role-badge">
                  <RoleIcon role={ceremony.role} size={14} /> {t.role.name[ceremony.role]}
                </span>
              </div>
            </div>
            <div className="ceremony-name">
              {players.find((p) => p.uid === ceremony.uid)?.displayName ?? '???'}
            </div>
          </div>
        </div>
      )}
      {/* Flatlined: don't lose the player — offer an instant fresh run. */}
      {me && !me.alive && !game.winner && !flatlineSeen && (
        <div className="flatline-panel">
          <div className="flatline-title">💀 {t.table.flatTitle}</div>
          <span className="coach-text">{t.table.flatBody}</span>
          <div className="row" style={{ gap: 8, width: '100%' }}>
            <button className="btn small block ghost" onClick={() => setFlatlineSeen(true)}>
              {t.table.flatWatch}
            </button>
            <button
              className="btn small block lime"
              disabled={session.busy}
              onClick={async () => {
                await session.leaveToHome();
                session.quickPlay();
              }}
            >
              ⚡ {t.table.flatNew}
            </button>
          </div>
        </div>
      )}
      {splat && (
        <div className="dmg-splat" key={splat.id} aria-hidden>
          <span>-{splat.amount}</span>
        </div>
      )}
      {/* Comms: on wide screens an always-open rail on the right (feed + emoji
          palette + input); on narrow screens a floating toggle with the same
          content in a popup panel. */}
      {isLandscape ? (
        <div className="table-side">
          <CommsRail
            feed={session.feed}
            nameOf={(uid) => players.find((p) => p.uid === uid)?.displayName ?? '???'}
            onSend={session.sendEmote}
            onMessage={session.sendMessage}
          />
        </div>
      ) : (
        <EmoteBar
          feed={session.feed}
          nameOf={(uid) => players.find((p) => p.uid === uid)?.displayName ?? '???'}
          onSend={session.sendEmote}
          onMessage={session.sendMessage}
        />
      )}
      <ScreenFlash events={session.screenFx} />
    </div>
  );
}

/** Which accent a system line gets in the feed, by event family. */
function logTone(line: string): 'hit' | 'good' | 'sys' {
  const ev = parseLogEvent(line);
  if (!ev) return 'sys';
  switch (ev.t) {
    case 'spike': case 'duel': case 'raid': case 'siphon': case 'burst':
    case 'dmg': case 'surge': case 'lash': case 'leech': case 'virus':
    case 'virusJump': case 'flat': case 'trace': case 'emp': case 'plant':
    case 'penalty': case 'toHit':
      return 'hit';
    case 'shield': case 'fw': case 'mayFw': case 'med': case 'pay':
    case 'stim': case 'reboot': case 'regen': case 'equip': case 'ride':
    case 'bounty': case 'dtFree': case 'salvage': case 'win':
      return 'good';
    default:
      return 'sys';
  }
}

/** Sentinel wrapped around player names inside rendered log copy so they can be
 *  emphasised without forking the i18n templates. Never occurs in real text. */
const NAME_MARK = '\u0000';

/** One feed entry: a system action (accent by tone, names highlighted) or a
 *  player chat line. */
function FeedLine({ item, nameOf }: { item: FeedItem; nameOf: (uid: string) => string }) {
  const t = useT();
  if (item.line !== undefined) {
    const text = renderLogLine(t, item.line, (uid) => NAME_MARK + nameOf(uid) + NAME_MARK);
    return (
      <div className={'line feed-sys feed-' + logTone(item.line)}>
        {text.split(NAME_MARK).map((seg, i) => (i % 2 ? <b key={i} className="feed-name">{seg}</b> : seg))}
      </div>
    );
  }
  const body = item.text ?? (item.lineKey ? t.botLines[item.lineKey] : item.emoji);
  if (!body) return null;
  return (
    <div className="line feed-chat">
      <b className="feed-nick">{nameOf(item.uid ?? '')}:</b> {body}
    </div>
  );
}


/** Anti-misfire hold-to-confirm button: must be held for HOLD_MS — a neon fill
 *  sweeps across while held; a quick tap just flashes the "hold" hint. */
const HOLD_MS = 600;
function HoldButton({
  label,
  hint,
  disabled,
  className,
  onConfirm,
}: {
  label: string;
  hint: string;
  disabled?: boolean;
  className?: string;
  onConfirm: () => void;
}) {
  const [holding, setHolding] = useState(false);
  const [hinting, setHinting] = useState(false);
  const holdTimer = useRef<number | null>(null);
  const hintTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (holdTimer.current !== null) clearTimeout(holdTimer.current);
    if (hintTimer.current !== null) clearTimeout(hintTimer.current);
  }, []);

  function start() {
    if (disabled || holdTimer.current !== null) return;
    setHinting(false);
    setHolding(true);
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      setHolding(false);
      vibrate(25);
      onConfirm();
    }, HOLD_MS);
  }

  function cancel() {
    if (holdTimer.current !== null) {
      // Released before the fill completed — nudge with the "hold" hint.
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
      setHinting(true);
      if (hintTimer.current !== null) clearTimeout(hintTimer.current);
      hintTimer.current = window.setTimeout(() => setHinting(false), 1200);
    }
    setHolding(false);
  }

  return (
    <button
      className={'btn hold' + (className ? ' ' + className : '') + (holding ? ' holding' : '')}
      style={{ ['--hold-ms' as string]: HOLD_MS + 'ms' }}
      disabled={disabled}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="hold-fill" aria-hidden />
      <span className="hold-label">{hinting ? hint : label}</span>
    </button>
  );
}

function ActionBar({
  session,
  selected,
  excessDiscard,
  discards,
  onDeploy,
  onEndTurn,
  onConfirmDiscard,
}: {
  session: GameSession;
  selected: Card | null;
  excessDiscard: number;
  discards: Set<string>;
  onDeploy: () => void;
  onEndTurn: () => void;
  onConfirmDiscard: () => void;
}) {
  const t = useT();
  const { game, isMyTurn, busy } = session;
  if (!game || game.winner) return null;

  if (!isMyTurn) {
    return <div className="mono-dim" style={{ textAlign: 'center' }}>{t.table.waiting}</div>;
  }
  if (game.phase === 'draw') {
    // The draw step is handled by the centered DrawPrompt overlay.
    return null;
  }
  if (game.phase === 'discard') {
    return (
      <button
        className="btn block magenta"
        disabled={busy || discards.size < excessDiscard}
        onClick={onConfirmDiscard}
      >
        {t.table.confirm} ({discards.size}/{excessDiscard})
      </button>
    );
  }
  const needTarget = selected && needsTarget(selected.kind);
  // Cancelling a selection is done by tapping the highlighted card again, so no
  // explicit Cancel button is needed. When a target is required we also skip the
  // "select a target" label — the targetable seats light up, which says it.
  return (
    <div className="row" style={{ gap: 10 }}>
      <HoldButton
        className="ghost block"
        label={t.table.endTurn}
        hint={t.table.holdToEnd}
        disabled={busy}
        onConfirm={onEndTurn}
      />
      {selected && !needTarget && (
        <button className="btn block magenta" disabled={busy} onClick={onDeploy}>
          {t.table.deploy}
        </button>
      )}
    </div>
  );
}

function TurnTimer({ deadline }: { deadline: number }) {
  const t = useT();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, []);
  const left = Math.max(0, Math.ceil((deadline - now) / 1000));
  const label = left >= 60 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : `${left}${t.home.sec}`;
  return <span className={'phase-timer' + (left <= 10 ? ' low' : '')}>{label}</span>;
}

const TRASH_EMOJIS = ['😂', '💀', '🤡', '🔥', '😈', '🗑️', '👀', '🐍'];

/**
 * Compact faction roster: one chip per faction present, with a pip per member —
 * lit for those still in play, ✕ for confirmed flatlines. Totals are public
 * (fixed by player count); only deaths reveal who was who, so this never spoils.
 */
function RosterBar({ players }: { players: PublicPlayer[] }) {
  const t = useT();
  const composition = roleComposition[players.length];
  if (!composition) return null;

  const fallen = (role: Role) =>
    players.filter((p) => !p.alive && p.revealedRole === role).length;

  return (
    <div className="roster">
      <span className="roster-label eyebrow">{t.table.factions}</span>
      <div className="roster-chips">
        {roleOrder.map((role) => {
          const total = composition[role];
          if (total <= 0) return null;
          const down = fallen(role);
          return (
            <span
              key={role}
              className="roster-chip"
              style={{ color: roleColor[role], borderColor: roleColor[role] }}
              title={`${t.role.name[role]} · ${total - down}/${total}`}
            >
              <RoleIcon role={role} size={12} />
              <span className="roster-pips">
                {Array.from({ length: total }, (_, i) => (
                  <span key={i} className={'roster-pip' + (i < total - down ? ' live' : ' out')} />
                ))}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

type CommsProps = { onSend: (emoji: string) => void; onMessage: (text: string) => void };

/** Minimum gap between two outgoing messages/emotes — a light anti-spam guard
 *  that briefly locks the send controls after each send. */
const SEND_COOLDOWN_MS = 500;

/** The shared comms controls: an emoji palette and a short text field. */
function CommsControls({ onSend, onMessage }: CommsProps) {
  const t = useT();
  const [text, setText] = useState('');
  const [cooling, setCooling] = useState(false);
  const coolTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (coolTimer.current !== null) clearTimeout(coolTimer.current);
  }, []);

  function startCooldown() {
    setCooling(true);
    if (coolTimer.current !== null) clearTimeout(coolTimer.current);
    coolTimer.current = window.setTimeout(() => {
      coolTimer.current = null;
      setCooling(false);
    }, SEND_COOLDOWN_MS);
  }

  function emitEmoji(emoji: string) {
    if (cooling) return;
    onSend(emoji);
    startCooldown();
  }

  function submit() {
    const trimmed = text.trim();
    if (!trimmed || cooling) return;
    onMessage(trimmed);
    setText('');
    startCooldown();
  }

  return (
    <>
      <div className="emote-grid">
        {TRASH_EMOJIS.map((e) => (
          <button
            key={e}
            className="emote-btn"
            disabled={cooling}
            onClick={() => emitEmoji(e)}
          >
            {e}
          </button>
        ))}
      </div>
      <form
        className="emote-say"
        onSubmit={(ev) => {
          ev.preventDefault();
          submit();
        }}
      >
        <input
          className="emote-input"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
          placeholder={t.table.say}
          maxLength={120}
          aria-label={t.table.comms}
        />
        <button type="submit" className="emote-send" disabled={!text.trim() || cooling} aria-label={t.table.send} title={t.table.send}>
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 12 L20 4 L15 20 L11 13 z" />
            <path d="M11 13 L20 4" />
          </svg>
        </button>
      </form>
    </>
  );
}

type FeedCommsProps = CommsProps & {
  feed: FeedItem[];
  nameOf: (uid: string) => string;
};

/** Scrolling feed history, pinned to the newest line. */
function FeedList({ feed, nameOf }: { feed: FeedItem[]; nameOf: (uid: string) => string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [feed]);
  if (feed.length === 0) return null;
  return (
    <div className="feed-lines" ref={ref}>
      {feed.map((item) => (
        <FeedLine key={item.id} item={item} nameOf={nameOf} />
      ))}
    </div>
  );
}

/** Always-open comms rail (wide screens): the feed fills the column, with the
 *  emoji palette and text field docked below it. */
function CommsRail({ feed, nameOf, ...comms }: FeedCommsProps) {
  const t = useT();
  return (
    <div className="comms-rail">
      <span className="eyebrow comms-rail-label">{t.table.comms}</span>
      <FeedList feed={feed} nameOf={nameOf} />
      <CommsControls {...comms} />
    </div>
  );
}

/** Comms dock for narrow screens: a floating toggle that opens the feed with
 *  the emoji palette and text field below it. */
function EmoteBar({ feed, nameOf, ...comms }: FeedCommsProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="emote-bar">
      {open && (
        <div className="emote-panel">
          <FeedList feed={feed} nameOf={nameOf} />
          <CommsControls {...comms} />
        </div>
      )}
      <button
        className={'emote-toggle' + (open ? ' open' : '')}
        onClick={() => setOpen((o) => !o)}
        aria-label={t.table.comms}
      >
        {open ? '✕' : '🗯️'}
      </button>
    </div>
  );
}

/** Raid/Siphon choice: rip a specific face-up gear card, or take a random one
 *  from the hidden hand. Only shown when the target actually has gear in play. */
function RobPicker({
  card,
  target,
  allowHand,
  onPick,
  onClose,
}: {
  card: Card;
  target: PublicPlayer;
  allowHand: boolean;
  onPick: (targetCardId?: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <div className="modal-backdrop rob-backdrop" onClick={onClose}>
      <div className="panel rob-panel" onClick={(e) => e.stopPropagation()}>
        <div className="row between rob-head">
          <div>
            <h2 className="h2">{t.card.name[card.kind]}</h2>
            <div className="mono-dim rob-sub">{t.table.robPrompt(target.displayName)}</div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>

        <div className="rob-caster-card">
          <CardFace card={card} disabled />
        </div>

        <div className="rob-grid">
          {target.equippedGear.map((g) => (
            <button
              key={g.id}
              className="rob-card-choice"
              style={{ ['--accent' as string]: cardSuit[g.kind].color }}
              onClick={() => onPick(g.id)}
            >
              <span className="rob-card-art" style={{ backgroundImage: `url(${cardArt(g.kind)})` }}>
                <span><CardIcon kind={g.kind} size={16} /></span>
              </span>
              <strong>{t.card.name[g.kind]}</strong>
              <small>{t.card.short[g.kind]}</small>
            </button>
          ))}
          {allowHand && (
            <button
              className="rob-card-choice rob-hand-choice"
              disabled={target.handCount === 0}
              onClick={() => onPick(undefined)}
            >
              <span className="rob-hand-stack" aria-hidden>
                <CardBack />
                <CardBack />
                <CardBack />
              </span>
              <strong>{t.table.robHand}</strong>
              <small>{target.handCount}</small>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function GiftPicker({
  card,
  target,
  hand,
  onPick,
  onClose,
}: {
  card: Card;
  target: PublicPlayer;
  hand: Card[];
  onPick: (cardId: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <div className="modal-backdrop rob-backdrop" onClick={onClose}>
      <div className="panel rob-panel" onClick={(e) => e.stopPropagation()}>
        <div className="row between rob-head">
          <div>
            <h2 className="h2">{t.card.name[card.kind]}</h2>
            <div className="mono-dim rob-sub">{t.table.giftPrompt(target.displayName)}</div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>
        <div className="choice-card-grid">
          {hand.map((giftCard) => (
            <button key={giftCard.id} className="choice-card" onClick={() => onPick(giftCard.id)}>
              <CardFace card={giftCard} disabled />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function RebootPicker({
  card,
  hand,
  onConfirm,
  onClose,
}: {
  card: Card;
  hand: Card[];
  onConfirm: (cardIds: string[]) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else if (next.size < 3) next.add(id);
    return next;
  });
  return (
    <div className="modal-backdrop rob-backdrop" onClick={onClose}>
      <div className="panel rob-panel" onClick={(e) => e.stopPropagation()}>
        <div className="row between rob-head">
          <div>
            <h2 className="h2">{t.card.name[card.kind]}</h2>
            <div className="mono-dim rob-sub">{t.table.rebootPrompt}</div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>
        <div className="choice-card-grid">
          {hand.map((choice) => (
            <button
              key={choice.id}
              className={'choice-card' + (selected.has(choice.id) ? ' selected' : '')}
              onClick={() => toggle(choice.id)}
            >
              <CardFace card={choice} disabled />
            </button>
          ))}
        </div>
        <button className="btn block violet" onClick={() => onConfirm([...selected])}>
          {t.table.rebootConfirm(selected.size)}
        </button>
      </div>
    </div>
  );
}

function DuelFighter({ player, isLoser }: { player?: PublicPlayer; isLoser: boolean }) {
  const portrait = characterArt(player?.characterId);
  return (
    <div className={'duel-fighter' + (isLoser ? ' loser' : '')}>
      <span className="duel-portrait">
        {portrait ? (
          <img src={portrait} alt="" />
        ) : player?.photoURL ? (
          <img src={player.photoURL} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span className="duel-portrait-fallback">{initials(player?.displayName ?? '?')}</span>
        )}
      </span>
      <span className="duel-fighter-name">{player?.displayName ?? '???'}</span>
    </div>
  );
}

function DuelMomentOverlay({ beat, players }: { beat: DuelBeat; players: PublicPlayer[] }) {
  const t = useT();
  const attacker = players.find((p) => p.uid === beat.attackerUid);
  const target = players.find((p) => p.uid === beat.targetUid);
  const loser = players.find((p) => p.uid === beat.loserUid);
  return (
    <div className="modal-backdrop duel-backdrop duel-moment-backdrop" aria-live="polite">
      <div className="panel duel-panel duel-moment-panel">
        <span className="eyebrow duel-moment-title">{t.card.name.duel}</span>

        <div className="duel-moment-stage">
          <div className="duel-pulse" aria-hidden />
          <div className="duel-fighters">
            <DuelFighter player={attacker} isLoser={beat.loserUid === beat.attackerUid} />
            <span className="duel-vs">VS</span>
            <DuelFighter player={target} isLoser={beat.loserUid === beat.targetUid} />
          </div>
        </div>

        <div className="duel-result">
          <strong>{loser?.displayName ?? '???'}</strong>
          <span className="mono-dim">-1</span>
        </div>
      </div>
    </div>
  );
}

/** Centered turn-start prompt: it's your turn and you must draw to begin. */
function DrawPrompt({ session }: { session: GameSession }) {
  const t = useT();
  const { busy } = session;
  return (
    <div className="modal-backdrop draw-prompt-backdrop">
      <div className="draw-prompt" onClick={(e) => e.stopPropagation()}>
        <span className="eyebrow draw-prompt-eyebrow">{t.table.yourTurnTitle}</span>
        {session.game?.turnDeadline ? (
          <DrawTimer deadline={session.game.turnDeadline} />
        ) : (
          <div className="draw-prompt-cards" aria-hidden>
            <CardBack />
            <CardBack />
          </div>
        )}
        <button className="btn block cyan" disabled={busy} onClick={session.drawCards}>
          {t.table.draw}
        </button>
      </div>
    </div>
  );
}

/** Shrinking ring + seconds count for the turn deadline, shown on the DrawPrompt. */
function DrawTimer({ deadline }: { deadline: number }) {
  const totalRef = useRef<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);
  if (totalRef.current === null) totalRef.current = Math.max(1000, deadline - Date.now());
  const total = totalRef.current;
  const leftMs = Math.max(0, deadline - now);
  const left = Math.ceil(leftMs / 1000);
  const frac = Math.max(0, Math.min(1, leftMs / total));
  const R = 44;
  const C = 2 * Math.PI * R;
  return (
    <div className={'draw-timer' + (left <= 10 ? ' low' : '')}>
      <svg viewBox="0 0 100 100" width={110} height={110} aria-hidden>
        <defs>
          <linearGradient id="drawTimerGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--cyan)" />
            <stop offset="100%" stopColor="#8affff" />
          </linearGradient>
          <linearGradient id="drawTimerGradLow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--magenta)" />
            <stop offset="100%" stopColor="#ff8ac0" />
          </linearGradient>
        </defs>
        <circle className="draw-timer-track" cx="50" cy="50" r={R} />
        <circle
          className="draw-timer-fill"
          cx="50"
          cy="50"
          r={R}
          style={{ strokeDasharray: C, strokeDashoffset: C * (1 - frac) }}
        />
      </svg>
      <span className="draw-timer-num">{left}</span>
    </div>
  );
}

function RespondOverlay({ session }: { session: GameSession }) {
  const t = useT();
  const { game, players, myFirewall, myAirGap, busy, myUid } = session;
  const pending = game?.pendingAttack ?? null;
  const attacker = pending ? players.find((p) => p.uid === pending.attackerUid) : null;
  const target = pending ? players.find((p) => p.uid === pending.targetUid) : null;
  const effectKind = pending?.effectKind ?? null;
  const threat: Card = { id: 'respond-threat-0', kind: effectKind ?? 'hack' };
  const responseCard = effectKind ? myAirGap : myFirewall;
  const responseLabel = effectKind ? t.respond.airGap : t.respond.firewall;
  return (
    <div className="modal-backdrop duel-backdrop">
      <div className="panel duel-panel" onClick={(e) => e.stopPropagation()}>
        <div className="duel-versus">
          <div className="duel-combatant defender">
            <span>{target?.uid === myUid ? t.player.you : target?.displayName ?? t.player.you}</span>
          </div>
          <div className="duel-vs">VS</div>
          <div className="duel-combatant attacker">
            <span>{attacker?.displayName ?? '???'}</span>
          </div>
        </div>

        <h2 className="h2 duel-title">
          {effectKind ? t.respond.effectTitle(t.card.name[effectKind]) : t.respond.title}
        </h2>
        {/* The turn deadline keeps ticking while you decide — show it. */}
        {game?.turnDeadline && <DrawTimer deadline={game.turnDeadline} />}

        <div className="duel-card-row">
          <div className="duel-threat">
            <CardFace card={threat} disabled />
            <span className="mono-dim">{attacker?.displayName ?? '???'}</span>
          </div>
          {responseCard && (
            <button className="duel-response-card" disabled={busy} onClick={() => session.respond(responseCard)}>
              <CardFace card={responseCard} disabled />
              <span>{responseLabel}</span>
            </button>
          )}
        </div>

        <div className="row duel-actions">
          <button className="btn ghost block" disabled={busy} onClick={() => session.respond(null)}>
            {effectKind ? t.respond.letThrough : t.respond.takeHit}
          </button>
          {responseCard && (
            <button className="btn cyan block" disabled={busy} onClick={() => session.respond(responseCard)}>
              {responseLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
